<script>
import Select from "../Components/Select.vue";
import DatePicker from "../Components/DatePicker.vue";
import Travelers from "../Components/FlightTravelers.vue";
import { BreakPoints, internalFlightRoutes } from "../../../Constants";
import { flightLocationMapper } from "../../../Mappers";
import { dateFormat, checkDateDiff } from "../../../Helpers/tools";

export default {
  components: {
    Select,
    DatePicker,
    Travelers,
  },

  data() {
    return {
      internalFlightRoutes,
      activeSection: internalFlightRoutes[0].value,
      locationSearchUrl: null,
      defaultParams: {
        originPlace: true,
      },
      submitData: {
        origin: null,
        destination: null,
        departureDate: null,
        returnDate: null,
        travelers: null,
        cabinType: null,
      },
      defaultCityList: [],
      isLoading: false,
    };
  },

  computed: {
    isRoundTrip() {
      return this.activeSection === this.internalFlightRoutes[1].value;
    },

    dateDiff() {
      return this.submitData.departureDate && this.submitData.returnDate
        ? checkDateDiff({
          firstDate: this.submitData.returnDate,
          secondDate: this.submitData.departureDate,
        })
        : null;
    },

    isFormValid() {
      return Object.values(this.$refs).every((ref) => ref?.isValid);
    }
  },

  methods: {
    flightLocationMapper,

    fillNextInput(type) {
      if (window.innerWidth <= BreakPoints.md) return;
      const inputOrder = [
        "origin",
        "destination",
        "datespicker",
      ];
      // Handle the case when origin and destination are the same
      if (type === "origin" || type === "destination") {
        const otherType = type === "origin" ? "destination" : "origin";
        if (
          this.submitData[type]?.code?.toLowerCase() ===
          this.submitData[otherType]?.code?.toLowerCase()
        ) {
          this.$refs[otherType]?.handleResetValue();
          this.$refs[otherType].handleOpen();
          return;
        }
      }
      // Find the next empty input
      const nextInput = inputOrder.find((input) => !this.submitData[input]);

      if (nextInput) {
        this.$refs[nextInput]?.handleOpen();
      }
    },

    handleReverseLocation() {
      const { origin, destination } = this.submitData;

      this.$refs.origin.handleSelectItem(destination);
      this.$refs.destination.handleSelectItem(origin);
    },

    validateAllInputs() {
      Object.values(this.$refs).forEach((ref) => ref?.validate());
    },

    buildSearchUrl() {
      const {
        origin,
        destination,
        departureDate,
        returnDate,
        travelers,
        cabinType,
      } = this.submitData;
      const { internFlightSearchUrl } = window.bookingSectionInitialize;
      let searchUrl = `${internFlightSearchUrl}${origin.code}-${destination.code}/?cab=${cabinType.cabinTypeNumber}&adt=${travelers.adult}&chd=${travelers.child}&inf=${travelers.infant}&dep=${departureDate}`;

      if (this.isRoundTrip) searchUrl += `&ret=${returnDate}`;

      return searchUrl;
    },

    async handleSearch() {
      this.validateAllInputs();

      if (this.isFormValid) {
        this.isLoading = true;

        try {
          const searchUrl = this.buildSearchUrl();
          window.location.assign(searchUrl);
        } catch (error) {
          console.error("Error during search:", error);
          this.isLoading = false;
        }
      }
    },
    datepickerValueHandler(val) {
      if (this.isRoundTrip) {
        this.submitData.departureDate = val[0];
        this.submitData.returnDate = val[1];
      }
      else this.submitData.departureDate = val;
    },
  },

  created() {
    if (window.internalCityList?.length) {
      this.defaultCityList = this.flightLocationMapper({
        model: {
          results: window.internalCityList,
        },
      });
    }

    this.locationSearchUrl =
      window.bookingSectionInitialize.internalFlightLocationSearchUrl;
  },
};
</script>

<template>
  <div>
    <ul class="booking__type">
      <li class="booking__type-item" v-for="item in internalFlightRoutes" :key="item.key">
        <input type="radio" class="checkmark" v-model="activeSection" :id="item.key" :value="item.value"
          autocomplete="off" name="internal-flight-active-section" />

        <label :for="item.key"> {{ item.title }}</label>
      </li>
    </ul>

    <div class="booking__main">
      <div class="booking__row">
        <div class="booking__items">
          <div class="booking__location booking__location--xl">
            <button class="booking__location-reverse icon-change" @click="handleReverseLocation" />

            <Select label="از" placeholder="انتخاب مبدا" name="origin" v-model="submitData.origin"
              :mapper="flightLocationMapper" :default-list="defaultCityList" :get-data-url="locationSearchUrl"
              :default-params="defaultParams" item-title="cityTitle" item-value="code" item-icon="icon"
              search-key="keyword" @fill-next-input="() => fillNextInput('origin')" ref="origin"
              list-header="شهرهای پرطرفدار" />

            <Select label="به" placeholder="انتخاب مقصد" name="destination" v-model="submitData.destination"
              :mapper="flightLocationMapper" :default-list="defaultCityList" :get-data-url="locationSearchUrl"
              :default-params="defaultParams" item-title="cityTitle" item-value="code" item-icon="icon"
              search-key="keyword" @fill-next-input="() => fillNextInput('destination')" ref="destination"
              list-header="شهرهای پرطرفدار" />
          </div>

          <DatePicker label="تاریخ رفت" :errorLabel="isRoundTrip ? 'تاریخ رفت و برگشت' : 'تاریخ رفت'"
            placeholder="انتخاب تاریخ رفت"
            :value="isRoundTrip ? [submitData.departureDate, submitData.returnDate] : submitData.departureDate"
            @input="datepickerValueHandler" @fill-next-input="() => fillNextInput('datespicker')" ref="datespicker"
            name="departureDate" :selectionMode="isRoundTrip ? 'range' : 'single'" :showSecondDate="true"
            secondLabel="تاریخ برگشت" secondPlaceholder="انتخاب تاریخ برگشت" />

          <div class="booking__travelers">
            <Travelers v-model="submitData.travelers" :cabinType.sync="submitData.cabinType" ref="travelers" />
          </div>
        </div>

        <button @click="handleSearch" class="booking__submit btn btn-red" title="جستجو" :disabled="isLoading">
          <span v-if="isLoading" class="bounce-loading bounce-loading--white">
            <span class="bounce1" />
            <span class="bounce2" />
            <span class="bounce3" />
          </span>

          <template v-else>
            <i class="icon-search ml--1" />

            <span>جستجو</span>
          </template>
        </button>
      </div>
    </div>
  </div>
</template>
